import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import CommonModal from "../../components/common/CommonModal";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Tickets.css";

function Tickets() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    attachment: null,
  });
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    fetchTickets(page);
  }, [page]);

  const fetchTickets = async (nextPage = 1) => {
    setLoading(true);

    try {
      const response = await api.get(`/tickets?page=${nextPage}`);
      if (response.data.success) {
        setTickets(response.data.data);
        setIsAdmin(response.data.is_admin);
        setLastPage(response.data.last_page);
        setTotal(response.data.total);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load tickets",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    setFormData((prev) => ({ ...prev, attachment: file }));
    setFileName(file.name);
    if (errors.attachment) {
      setErrors((prev) => ({ ...prev, attachment: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.subject.trim()) {
      nextErrors.subject = "Subject is required";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Message is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("subject", formData.subject);
      data.append("message", formData.message);
      if (formData.attachment) {
        data.append("attachment", formData.attachment);
      }

      const response = await api.post("/tickets", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Ticket Created!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        setShowModal(false);
        setFormData({ subject: "", message: "", attachment: null });
        setFileName("");
        setErrors({});
        fetchTickets(1);
        setPage(1);
      }
    } catch (error) {
      const serverErrors = error.response?.data?.errors || {};
      if (Object.keys(serverErrors).length > 0) {
        const mappedErrors = {};
        Object.keys(serverErrors).forEach((key) => {
          mappedErrors[key] = serverErrors[key][0];
        });
        setErrors(mappedErrors);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to create ticket",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "open":
        return "status-open";
      case "pending":
        return "status-pending";
      case "closed":
        return "status-closed";
      default:
        return "status-open";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return "fa-lock-open";
      case "pending":
        return "fa-clock";
      case "closed":
        return "fa-lock";
      default:
        return "fa-lock-open";
    }
  };

  const ticketColumns = [
    {
      header: "#ID",
      render: (ticket) => <span className="ticket-id">#{ticket.id}</span>,
    },
    ...(isAdmin
      ? [
          {
            header: "Raised By",
            render: (ticket) => (
              <div className="ticket-user">
                <div className="ticket-avatar">
                  {ticket.raised_by.charAt(0)}
                </div>
                <span>{ticket.raised_by}</span>
              </div>
            ),
          },
        ]
      : []),
    {
      header: "Subject",
      render: (ticket) => (
        <span className="ticket-subject">{ticket.subject}</span>
      ),
    },
    {
      header: "Status",
      render: (ticket) => (
        <span className={`status-badge ${getStatusClass(ticket.status)}`}>
          <i className={`fas ${getStatusIcon(ticket.status)}`}></i>
          {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
        </span>
      ),
    },
    {
      header: "Created At",
      render: (ticket) => (
        <span className="ticket-date">{ticket.created_at}</span>
      ),
    },
    {
      header: "Attachment",
      render: (ticket) =>
        ticket.attachment ? (
          <a
            href={ticket.attachment}
            target="_blank"
            rel="noreferrer"
            className="attachment-link"
          >
            <i className="fas fa-paperclip"></i> View
          </a>
        ) : (
          <span className="no-attachment">-</span>
        ),
    },
    {
      header: "Action",
      render: (ticket) => (
        <CommonActionMenu
          ariaLabel={`Actions for ticket ${ticket.id}`}
          actions={[
            {
              label: "View",
              icon: "fas fa-eye",
              onClick: () => navigate(`/tickets/${ticket.id}`),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="tickets-container">
        <div className="tickets-page-header">
          <div>
            <h4 className="tickets-page-title">
              <i className="fas fa-ticket-alt"></i>
              Support Tickets
            </h4>
            {!loading && (
              <p className="tickets-count">
                {total} ticket{total !== 1 ? "s" : ""} total
              </p>
            )}
          </div>
          <button
            className="btn-create-ticket"
            onClick={() => {
              setFormData({ subject: "", message: "", attachment: null });
              setErrors({});
              setFileName("");
              setShowModal(true);
            }}
          >
            <i className="fas fa-plus"></i>
            Create Ticket
          </button>
        </div>

        <div className="tickets-card">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading tickets...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="tickets-empty">
              <i className="fas fa-ticket-alt"></i>
              <h5>No tickets yet</h5>
              <p>Create your first support ticket to get help.</p>
              <button
                className="btn-create-ticket"
                onClick={() => setShowModal(true)}
              >
                <i className="fas fa-plus"></i>
                Create Ticket
              </button>
            </div>
          ) : (
            <>
              <CommonTable
                columns={ticketColumns}
                data={tickets}
                tableClassName="tickets-table"
              />

              {lastPage > 1 && (
                <div className="tickets-pagination">
                  <button
                    className="page-btn"
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={page === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  {Array.from({ length: lastPage }, (_, index) => index + 1).map(
                    (pageNumber) => (
                      <button
                        key={pageNumber}
                        className={`page-btn ${page === pageNumber ? "active" : ""}`}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ),
                  )}
                  <button
                    className="page-btn"
                    onClick={() =>
                      setPage((current) => Math.min(lastPage, current + 1))
                    }
                    disabled={page === lastPage}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {showModal && (
          <CommonModal
            onClose={() => setShowModal(false)}
            title={
              <>
                <i className="fas fa-ticket-alt"></i>
                Create New Ticket
              </>
            }
            overlayClassName="modal-backdrop-custom"
            contentClassName="ticket-modal"
            headerClassName="ticket-modal-header"
            bodyClassName="ticket-modal-body"
            footerClassName="ticket-modal-footer"
            closeButtonClassName="close-modal-btn"
            footer={
              <CommonFormActions
                className="ticket-modal-footer"
                cancelLabel="Cancel"
                submitLabel={
                  submitting ? (
                    <>
                      <span className="btn-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Submit Ticket
                    </>
                  )
                }
                onCancel={() => setShowModal(false)}
                onSubmit={handleSubmit}
                submitType="button"
                cancelClassName="btn-tm-cancel"
                submitClassName="btn-tm-submit"
                submitDisabled={submitting}
              />
            }
          >
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="tm-form-group">
                    <label>
                      Subject <span className="required">*</span>
                    </label>
                    <div
                      className={`tm-input-box ${errors.subject ? "error" : ""}`}
                    >
                      <i className="fas fa-heading tm-icon"></i>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Brief description of the issue"
                      />
                    </div>
                    {errors.subject && (
                      <span className="tm-error">
                        <i className="fas fa-exclamation-circle"></i>
                        {errors.subject}
                      </span>
                    )}
                  </div>

                  <div className="tm-form-group">
                    <label>
                      Message <span className="required">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Describe your issue in detail..."
                      rows="5"
                      className={errors.message ? "error" : ""}
                    ></textarea>
                    {errors.message && (
                      <span className="tm-error">
                        <i className="fas fa-exclamation-circle"></i>
                        {errors.message}
                      </span>
                    )}
                  </div>

                  <div className="tm-form-group">
                    <label>
                      Attachment <span className="optional">(optional)</span>
                    </label>
                    <label className="file-upload-area">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                        style={{ display: "none" }}
                      />
                      {fileName ? (
                        <div className="file-selected">
                          <i className="fas fa-file-check"></i>
                          <span>{fileName}</span>
                        </div>
                      ) : (
                        <div className="file-placeholder">
                          <i className="fas fa-cloud-upload-alt"></i>
                          <span>Click to upload</span>
                          <small>JPG, PNG, PDF, DOC up to 2MB</small>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </form>
          </CommonModal>
        )}
      </div>
    </MainLayout>
  );
}

export default Tickets;
